/* eslint-disable i18next/no-literal-string */
import { cn } from "~/lib/utils";
import { LandingPageHeaderH1 } from "~/components/landing/LandingText";
import HumLogo from "~/components/hum-by-verizon/HumLogo";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const humHeroBgPortrait = imageUrl("/images/hum-by-verizon/hum-hero-bg-portrait.png");
const humHeroBgLandscape = imageUrl("/images/hum-by-verizon/hum-hero-bg-landscape.png");

/**
 * Ported from bouncieweb's `LandingPageComponents/HumHero.js`.
 *
 * A rounded hero card: headline and subhead on the left, a bordered plate with
 * the Hum wordmark and the shutdown date. Unlike TuroHero/LoanerHero the card
 * has no fixed height — the original sizes it entirely from its content, so no
 * height is set here either.
 *
 * The `#896645` fill behind the image is the photo's average color; it smooths
 * over the load delay, so it is preserved (same rationale as LoanerHero's
 * `bg-[#7b8281]`).
 *
 * Two client-only branches from the original are Tailwind classes here, because
 * `useWindowSize()` returns `[]` on first render and would flash and mismatch on
 * hydration:
 *   - the portrait/landscape crop swap at 640px becomes the two-`<img>`
 *     `sm:hidden` / `hidden sm:block` pair (copied from LoanerHero);
 *   - the 145px -> 204px logo width at 1024px becomes `lg:w-[204px]`.
 *
 * The doubled `drop-shadow` on the text and logo groups is intentional in the
 * original — it stacks two identical shadows to deepen them over the photo.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  sm 640px -> sm:  md 768px -> md:
 *   lg 1024px -> lg:  xlg 1280px -> xl:
 */

// DropShadowWrapper
const DROP_SHADOW =
  "[filter:drop-shadow(0px_0px_30px_#000000)_drop-shadow(0px_0px_30px_#000000)]";

export default function HumHero() {
  return (
    // HeroWrapper
    <div className="mt-[26px] mb-12 flex justify-center overflow-hidden sm:mx-[50px] sm:mt-8 md:mb-20">
      {/* HeroRectangle */}
      <div
        className={cn(
          "relative w-full overflow-hidden text-white",
          "bg-[#896645]",
          "sm:rounded-[20px] md:flex xl:max-w-[1400px]",
        )}
      >
        {/* HeroBgImage, portrait crop — hidden from sm up */}
        <Picture
          src={humHeroBgPortrait}
          loading="eager"
          alt="A car on a highway driving toward a sunset"
          className={cn(
            "absolute h-full w-full max-w-none object-cover",
            "object-[bottom_right] min-[431px]:object-[center_right]",
            "sm:hidden",
          )}
        />
        {/* HeroBgImage, landscape crop — hidden below sm */}
        <Picture
          src={humHeroBgLandscape}
          loading="eager"
          alt="A car on a highway driving toward a sunset"
          className={cn(
            "absolute hidden h-full w-full max-w-none object-cover",
            "sm:block sm:object-[center_center] md:object-[60%_0%] lg:object-[center_center]",
          )}
        />

        {/* DimmerMobile — mobile-only scrim */}
        <div className="absolute top-0 left-0 h-full w-full bg-black/[0.21] md:hidden" />

        {/* ContentArea */}
        <div className="relative m-12 flex flex-col items-start md:ml-[34px] lg:mt-[120px] lg:ml-[75px] lg:w-full xl:ml-[120px]">
          {/* TextSection */}
          <div className="max-w-[400px] md:max-w-[500px] lg:w-full lg:max-w-none">
            <div className={DROP_SHADOW}>
              {/* HeroHeader (LandingPageHeaderH1 + hero overrides). The
                  original's own font sizes win over the shared header's at
                  every breakpoint: 10vw, then 40px from 431px, then 54px from
                  1024px. */}
              <LandingPageHeaderH1
                className={cn(
                  "mt-0 mb-6 text-left leading-[1.2]",
                  "text-[10vw] min-[431px]:text-[40px] md:text-[40px]",
                  "lg:mb-0 lg:text-[54px]",
                )}
              >
                Beyond the Hum Shutdown
              </LandingPageHeaderH1>
              {/* HeroSubHeader */}
              <p className="font-display mt-0 mb-[90px] text-[28px] leading-[1.2] font-bold lg:mb-[45px] lg:text-[40px] xl:mb-0">
                Bouncie, the new way forward.
              </p>
            </div>
          </div>

          {/* LogoSection */}
          <div className="inline-block overflow-hidden rounded-lg border border-white bg-[#00000044] p-4 lg:self-end">
            <div className={DROP_SHADOW}>
              <HumLogo className="h-auto w-[145px] lg:w-[204px]" />
              {/* HumTagline */}
              <div className="font-display text-[12px] font-bold lg:text-[18px]">
                Service Ends Dec. 31, 2025
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
